"use client";

import { IExchangeRatesMapResponse } from "@finbackoffice/clientbff-client";
import { Currency } from "@finbackoffice/enums";
import { useRuntimeConfig, useWebsocketUpdates } from "@finbackoffice/site-core";
import {
    WalletsContext,
    ExchangeRatesContextV2,
    UserAccountContextV2,
    ConfigContextV2,
} from "contexts";
import { useCallback, useContext, useMemo } from "react";

export const useLatestExchangeRates = (): void => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { siteDefaultCurrency } = useContext(ConfigContextV2);
    const { siteSupportedWalletCurrencies } = useContext(WalletsContext);
    const { userCurrency } = useContext(UserAccountContextV2);
    const { setExchangeRatesData } = useContext(ExchangeRatesContextV2);

    const handler = useCallback(
        (exchangeRates: IExchangeRatesMapResponse) => {
            if (exchangeRates) {
                setExchangeRatesData(exchangeRates);
            }
        },
        [setExchangeRatesData],
    );

    const subscriptionParams = useMemo(() => {
        const fiatEnabled = COMMON_SITE_CONFIGS.wallet.fiat;
        const fxCurrency = COMMON_SITE_CONFIGS.wallet.fxCurrency;
        const toCurrencies = [];

        if (fiatEnabled) {
            toCurrencies.push(Currency.USD);
        } else if (fxCurrency) {
            toCurrencies.push(fxCurrency);
        }

        if (fiatEnabled && siteDefaultCurrency && !toCurrencies.includes(siteDefaultCurrency)) {
            toCurrencies.push(siteDefaultCurrency);
        }

        if (userCurrency && !toCurrencies.includes(userCurrency)) {
            toCurrencies.push(userCurrency);
        }

        return {
            fromCurrency: siteSupportedWalletCurrencies,
            toCurrencies,
        };
    }, [
        siteDefaultCurrency,
        userCurrency,
        siteSupportedWalletCurrencies,
        COMMON_SITE_CONFIGS.wallet,
    ]);

    useWebsocketUpdates("exchange_rates_latest", handler, undefined, subscriptionParams);
};
